// Cold treatment (USA) - the reads and writes behind the Coldroom tab and its print page.
import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { fetchAllPaged } from '@/lib/fetchAllPaged'
import { withTimeout } from '@/lib/saveTimeout'
import { POLL } from '@/lib/pollTiers'
import type {
  ColdTreatmentLot, ColdTreatmentProbe, ColdTreatmentReading, ColdTreatmentStatus,
} from '@/lib/coldTreatment'

const LOT_COLS = 'lot_id, lot_no, coldroom_id, threshold_c, required_hours, final_hours, note, started_at, recorded_by'
const PROBE_COLS = 'probe_id, lot_id, probe_no, equipment_id, pallet_id, position, calibration_due_on, retired_at, retired_reason, retired_by, created_at'
const READING_COLS = 'reading_id, lot_id, probe_id, read_at, pulp_temp_c, note, recorded_by, supersedes_id'

export interface CtRoom { coldroom_id: string; code: string; name: string; is_active: boolean; usda_approved: boolean }
export interface CtEquipment {
  equipment_id: string; asset_tag: string; name: string; status: string
  next_calibration_due: string | null; calibration_band: string | null
}
export interface CtRoomPallet { pallet_id: string; carton_count: number | null; coldroom_grid: string | null; grower: string | null; variety: string | null }

export const CT_KEYS = {
  rooms: ['ct-rooms'], lots: ['ct-lots'], equipment: ['ct-equipment'],
  lot: (id: string) => ['ct-lot', id], status: (id: string) => ['ct-status', id],
  roomPallets: (room: string) => ['ct-room-pallets', room],
  probeLabels: (ids: string) => ['ct-probe-labels', ids],
}

export async function fetchCtRooms(): Promise<CtRoom[]> {
  const { data, error } = await withTimeout(supabase.from('coldrooms')
    .select('coldroom_id, code, name, is_active, usda_approved').order('sort_order').order('code'), 'ColdTreatment: rooms read')
  if (error) throw error
  return (data ?? []) as CtRoom[]
}

export async function fetchCtLots(): Promise<ColdTreatmentLot[]> {
  const { rows, error } = await fetchAllPaged<ColdTreatmentLot>((a, b) =>
    withTimeout(supabase.from('cold_treatment_lots').select(LOT_COLS).order('started_at', { ascending: false }).order('lot_id').range(a, b), 'ColdTreatment: lots read'))
  if (error) throw new Error(error)
  return rows
}

/** Equipment with its calibration band: the same view ComplianceEquipment reads. */
export async function fetchCtEquipment(): Promise<CtEquipment[]> {
  const { rows, error } = await fetchAllPaged<CtEquipment>((a, b) =>
    withTimeout(supabase.from('equipment_summary').select('equipment_id, asset_tag, name, status, next_calibration_due, calibration_band')
      .eq('status', 'ACTIVE').order('asset_tag').order('equipment_id').range(a, b), 'ColdTreatment: equipment read'))
  if (error) throw new Error(error)
  return rows
}

/** The asset tag and name of each probe's equipment (any status), so a record can be matched to the physical probe and its certificate. */
async function fetchCtProbeLabels(ids: string[]): Promise<Map<string, string>> {
  const { rows, error } = await fetchAllPaged<{ equipment_id: string; asset_tag: string; name: string }>((a, b) =>
    withTimeout(supabase.from('equipment_summary').select('equipment_id, asset_tag, name').in('equipment_id', ids)
      .order('equipment_id').range(a, b), 'ColdTreatment: probe equipment read'))
  if (error) throw new Error(error)
  return new Map(rows.map(r => [r.equipment_id, `${r.asset_tag} · ${r.name}`]))
}

/** equipment_id to "asset tag · name" for these probes. Falls back to a short id fragment until the read lands. */
export function useCtProbeLabels(probes: ColdTreatmentProbe[]): (equipmentId: string) => string {
  const ids = [...new Set(probes.map(p => p.equipment_id))].sort()
  const q = useQuery({ queryKey: CT_KEYS.probeLabels(ids.join(',')), queryFn: () => fetchCtProbeLabels(ids), enabled: ids.length > 0 })
  return (id: string) => q.data?.get(id) ?? id.slice(0, 8)
}

export async function fetchCtRoomPallets(roomId: string): Promise<CtRoomPallet[]> {
  const { rows, error } = await fetchAllPaged<Record<string, unknown>>((a, b) =>
    withTimeout(supabase.from('pallets').select('pallet_id, carton_count, coldroom_grid, runs:run_id(grower, variety)')
      .eq('status', 'IN_COLDROOM').eq('coldroom_id', roomId).is('deleted_at', null).order('pallet_id').range(a, b), 'ColdTreatment: room pallets read'))
  if (error) throw new Error(error)
  return rows.map(r => {
    const run = r.runs as { grower: string; variety: string } | null
    return {
      pallet_id: r.pallet_id as string, carton_count: (r.carton_count as number | null) ?? null,
      coldroom_grid: (r.coldroom_grid as string | null) ?? null, grower: run?.grower ?? null, variety: run?.variety ?? null,
    }
  })
}

export async function fetchCtStatus(lotId: string): Promise<ColdTreatmentStatus> {
  const { data, error } = await withTimeout(supabase.rpc('cold_treatment_status', { p_lot_id: lotId }), 'ColdTreatment: status read')
  if (error) throw error
  if (!data) throw new Error('Lot not found')
  return data as ColdTreatmentStatus
}

export interface LotDetail {
  pallets: string[]
  probes: ColdTreatmentProbe[]
  readings: ColdTreatmentReading[]
}

async function fetchCtLotDetail(lotId: string): Promise<LotDetail> {
  const [pal, pro, rd] = await Promise.all([
    fetchAllPaged<{ pallet_id: string }>((a, b) =>
      withTimeout(supabase.from('cold_treatment_lot_pallets').select('pallet_id').eq('lot_id', lotId).order('pallet_id').range(a, b), 'ColdTreatment: lot pallets read')),
    fetchAllPaged<ColdTreatmentProbe>((a, b) =>
      withTimeout(supabase.from('cold_treatment_probes').select(PROBE_COLS).eq('lot_id', lotId).order('probe_no').range(a, b), 'ColdTreatment: probes read')),
    fetchAllPaged<ColdTreatmentReading>((a, b) =>
      withTimeout(supabase.from('cold_treatment_readings').select(READING_COLS).eq('lot_id', lotId)
        .order('read_at', { ascending: false }).order('reading_id').range(a, b), 'ColdTreatment: readings read')),
  ])
  if (pal.error) throw new Error(pal.error)
  if (pro.error) throw new Error(pro.error)
  if (rd.error) throw new Error(rd.error)
  return { pallets: pal.rows.map(r => r.pallet_id), probes: pro.rows, readings: rd.rows }
}

export function useCtStatus(lotId: string) {
  return useQuery({ queryKey: CT_KEYS.status(lotId), queryFn: () => fetchCtStatus(lotId), refetchInterval: POLL.standard })
}

export function useCtLotDetail(lotId: string) {
  return useQuery({ queryKey: CT_KEYS.lot(lotId), queryFn: () => fetchCtLotDetail(lotId), refetchInterval: POLL.standard })
}
